import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Form, Modal, ProgressBar, Row, Spinner } from "react-bootstrap";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import PreviewOutlinedIcon from "@mui/icons-material/PreviewOutlined";

import { fetchWithAuth } from "./FetchWithAuth";
import { url } from "./url";
import styles from "./CreateAppointmentWizard.module.scss";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  getDate,
  getDay,
  isBefore,
  isSameDay,
  isToday,
  startOfMonth,
} from "date-fns";

const TOTAL_STEPS = 4;

const STEP_META = [
  { id: 1, short: "Customer", title: "Customer & Enquiry", icon: <PersonOutlineRoundedIcon fontSize="small" /> },
  { id: 2, short: "Schedule", title: "Appointment Setup", icon: <EventOutlinedIcon fontSize="small" /> },
  { id: 3, short: "Notes", title: "Internal Notes", icon: <NotesOutlinedIcon fontSize="small" /> },
  { id: 4, short: "Review", title: "Review & Confirm", icon: <PreviewOutlinedIcon fontSize="small" /> },
];

const defaultForm = {
  enquiry_id: "",
  customer_name: "",
  customer_email: "",
  customer_phone: "",
  appointment_type_id: "",
  counsellor_id: "",
  start_time: "",
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
  notes_internal: "",
  is_private: true,
};

const endpoint = (path) => {
  const base = String(url).replace(/\/+$/, "");
  return `${base}/${String(path).replace(/^\/+/, "")}`;
};

function validationForStep(step, form) {
  const errors = {};
  if (step === 1) {
    if (!form.customer_name?.trim()) errors.customer_name = "Customer name is required.";
    if (!form.customer_email?.trim()) {
      errors.customer_email = "Customer email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.customer_email)) {
      errors.customer_email = "Enter a valid email.";
    }
  }
  if (step === 2) {
    if (!form.start_time) errors.start_time = "Start time is required.";
  }
  return errors;
}

function uniqueEnquiries(rows = []) {
  const seen = new Set();
  const output = [];
  rows.forEach((item) => {
    const id = Number(item?.id);
    if (!id || seen.has(id)) return;
    seen.add(id);
    output.push(item);
  });
  return output;
}

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const toLocalDateKey = (isoString, timeZone) => {
  if (!isoString) return "";
  const d = new Date(isoString);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);

  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  const day = parts.find((p) => p.type === "day")?.value;
  return y && m && day ? `${y}-${m}-${day}` : "";
};

const formatTime = (isoString, timeZone) => {
  if (!isoString) return "--";
  return new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone,
  }).format(new Date(isoString));
};

export default function CreateAppointmentWizard({
  show,
  onHide,
  username,
  types = [],
  onSubmit,
  isSubmitting = false,
}) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(defaultForm);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [counsellors, setCounsellors] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [enquirySearch, setEnquirySearch] = useState("");
  const [manualEntryEnabled, setManualEntryEnabled] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(startOfMonth(new Date()));
  const [availableSlots, setAvailableSlots] = useState([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [selectedDateKey, setSelectedDateKey] = useState("");
  const [selectedSlotStartUtc, setSelectedSlotStartUtc] = useState("");

  useEffect(() => {
    if (!show) return;
    setStep(1);
    setForm(defaultForm);
    setErrors({});
    setApiError("");
    setManualEntryEnabled(false);
    setCalendarMonth(startOfMonth(new Date()));
    setAvailableSlots([]);
    setIsLoadingSlots(false);
    setSelectedDateKey("");
    setSelectedSlotStartUtc("");
  }, [show]);

  useEffect(() => {
    if (!show) return;
    const loadMeta = async () => {
      setIsLoadingData(true);
      setApiError("");
      try {
        let users = [];
        try {
          const usersRes = await fetchWithAuth(endpoint("/get_users"), { method: "GET", credentials: "include" });
          users = await usersRes.json();
        } catch (_error) {
          users = [];
        }
        let counsellorRows = (Array.isArray(users) ? users : []).filter((item) =>
          ["Counsellor", "Visa Counsellor", "Visa&Application Counsellor", "Manager", "admin"].includes(item.role)
        );

        // For non-admin/reception users where /get_users may fail, fall back to current user.
        if (!counsellorRows.length && username) {
          try {
            const selfRes = await fetchWithAuth(endpoint(`/get_user/${encodeURIComponent(username)}`), {
              method: "GET",
              credentials: "include",
            });
            const self = await selfRes.json();
            if (self?.id) {
              counsellorRows = [self];
              setForm((prev) => ({ ...prev, counsellor_id: String(self.id) }));
            }
          } catch (_error) {
            // ignore, user can still type manually if needed
          }
        }
        // Default to current user as counsellor when they're in the list (e.g. "My Appointments").
        if (counsellorRows.length && username) {
          const selfCounsellor = counsellorRows.find((c) => String(c.username || "").toLowerCase() === String(username).toLowerCase());
          if (selfCounsellor?.id) {
            setForm((prev) => ({ ...prev, counsellor_id: String(selfCounsellor.id) }));
          }
        }
        setCounsellors(counsellorRows);

        const enquiryBuckets = [];
        try {
          // Works across roles (admin/counsellor/visa)
          const mineRes = await fetchWithAuth(
            endpoint(`/enquiry/get_new_enquiries?id=${encodeURIComponent(username || "")}&page=1&per_page=200`),
            { method: "GET", credentials: "include" }
          );
          const mineData = await mineRes.json();
          enquiryBuckets.push(Array.isArray(mineData?.enquiries) ? mineData.enquiries : []);
        } catch (_error) {
          enquiryBuckets.push([]);
        }

        try {
          // Admin/Reception endpoint with richer payload (includes assigned_to when available)
          const allRes = await fetchWithAuth(
            endpoint("/enquiry/get_all_enquiries?page=1&page_size=200&name=none&source=none&status=none"),
            { method: "GET", credentials: "include" }
          );
          const allData = await allRes.json();
          enquiryBuckets.push(Array.isArray(allData?.data) ? allData.data : []);
        } catch (_error) {
          enquiryBuckets.push([]);
        }

        const mergedEnquiries = uniqueEnquiries(enquiryBuckets.flat());
        setEnquiries(mergedEnquiries);
      } catch (error) {
        setApiError(error.message || "Failed to load wizard data.");
      } finally {
        setIsLoadingData(false);
      }
    };
    loadMeta();
  }, [show, username]);

  const filteredEnquiries = useMemo(() => {
    const search = enquirySearch.trim().toLowerCase();
    if (!search) return enquiries.slice(0, 50);
    return enquiries
      .filter((item) => {
        const name = `${item.first_name || ""} ${item.last_name || ""}`.trim().toLowerCase();
        const email = String(item.email || "").toLowerCase();
        const phone = String(item.phone_number || "").toLowerCase();
        const idText = String(item.id || "");
        return name.includes(search) || email.includes(search) || phone.includes(search) || idText.includes(search);
      })
      .slice(0, 50);
  }, [enquiries, enquirySearch]);

  const effectiveCounsellorId = useMemo(
    () => Number(form.counsellor_id || username || 0) || null,
    [form.counsellor_id, username]
  );

  const fallbackAppointmentTypeId = useMemo(
    () => (Array.isArray(types) && types.length ? Number(types[0].id) : null),
    [types]
  );

  const slotsByDate = useMemo(() => {
    const zone = form.timezone || "UTC";
    const map = new Map();
    availableSlots.forEach((slot) => {
      const key = toLocalDateKey(slot.start_time_utc, zone);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(slot);
    });
    for (const slots of map.values()) {
      slots.sort((a, b) => new Date(a.start_time_utc) - new Date(b.start_time_utc));
    }
    return map;
  }, [availableSlots, form.timezone]);

  const availableDateKeys = useMemo(() => {
    const keys = new Set();
    for (const [dateKey, slots] of slotsByDate.entries()) {
      if (slots.some((slot) => Number(slot.available_capacity || 0) > 0)) {
        keys.add(dateKey);
      }
    }
    return keys;
  }, [slotsByDate]);

  const selectedDateSlots = useMemo(() => slotsByDate.get(selectedDateKey) || [], [slotsByDate, selectedDateKey]);

  const monthDays = useMemo(() => {
    const start = startOfMonth(calendarMonth);
    const end = endOfMonth(calendarMonth);
    const days = eachDayOfInterval({ start, end });
    const firstDay = getDay(start);
    const padded = Array(firstDay).fill(null);
    padded.push(...days);
    while (padded.length % 7 !== 0) padded.push(null);
    return padded;
  }, [calendarMonth]);

  const progressPercent = (step / TOTAL_STEPS) * 100;
  const resolvedStartTime = selectedSlotStartUtc || form.start_time || "";

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const onSelectEnquiry = (enquiry) => {
    const customerName = `${enquiry.first_name || ""} ${enquiry.last_name || ""}`.trim();
    setForm((prev) => ({
      ...prev,
      enquiry_id: enquiry.id || "",
      customer_name: customerName || prev.customer_name,
      customer_email: enquiry.email || prev.customer_email,
      customer_phone: enquiry.phone_number || prev.customer_phone,
      counsellor_id: enquiry.assigned_to ? String(enquiry.assigned_to) : prev.counsellor_id || "",
    }));
    setManualEntryEnabled(false);
  };

  useEffect(() => {
    if (step !== 2) {
      return;
    }
    if (!effectiveCounsellorId) {
      setAvailableSlots([]);
      setSelectedDateKey("");
      setSelectedSlotStartUtc("");
      setForm((prev) => ({ ...prev, start_time: "" }));
      return;
    }

    const loadAvailability = async () => {
      setIsLoadingSlots(true);
      try {
        const start = startOfMonth(calendarMonth);
        const end = endOfMonth(calendarMonth);
        const res = await fetchWithAuth(
          endpoint(
            `/appointments/availability?counsellor_id=${effectiveCounsellorId}&start_date=${format(start, "yyyy-MM-dd")}&end_date=${format(end, "yyyy-MM-dd")}&timezone=${encodeURIComponent(
              form.timezone || "UTC"
            )}`
          ),
          {
            method: "GET",
            credentials: "include",
          }
        );
        const data = await res.json();
        if (data?.error) {
          setAvailableSlots([]);
          setApiError(data.error);
          return;
        }
        const slots = Array.isArray(data?.slots) ? data.slots : [];
        setAvailableSlots(slots);
        if (slots.length === 0) setApiError("");
      } catch (error) {
        setAvailableSlots([]);
        setApiError(error.message || "Could not load slots.");
      } finally {
        setIsLoadingSlots(false);
      }
    };

    loadAvailability();
  }, [step, effectiveCounsellorId, form.timezone, calendarMonth]);

  useEffect(() => {
    if (!form.appointment_type_id && fallbackAppointmentTypeId) {
      setForm((prev) => ({ ...prev, appointment_type_id: String(fallbackAppointmentTypeId) }));
    }
  }, [form.appointment_type_id, fallbackAppointmentTypeId]);

  useEffect(() => {
    if (step !== 2) {
      return;
    }
    if (!availableDateKeys.size) {
      setSelectedDateKey("");
      setSelectedSlotStartUtc("");
      setField("start_time", "");
      return;
    }
    if (!selectedDateKey || !availableDateKeys.has(selectedDateKey)) {
      const first = Array.from(availableDateKeys).sort()[0];
      setSelectedDateKey(first);
      setSelectedSlotStartUtc("");
      setField("start_time", "");
    }
  }, [availableDateKeys, selectedDateKey, step]);

  useEffect(() => {
    if (step !== 2) {
      return;
    }
    if (!selectedSlotStartUtc) return;
    const stillExists = selectedDateSlots.some((slot) => slot.start_time_utc === selectedSlotStartUtc);
    if (!stillExists) {
      setSelectedSlotStartUtc("");
      setField("start_time", "");
    }
  }, [selectedDateSlots, selectedSlotStartUtc, step]);

  const goNext = () => {
    const formForValidation = step === 2 ? { ...form, start_time: resolvedStartTime } : form;
    const stepErrors = validationForStep(step, formForValidation);
    if (Object.keys(stepErrors).length) {
      setErrors((prev) => ({ ...prev, ...stepErrors }));
      return;
    }
    setStep((prev) => Math.min(prev + 1, TOTAL_STEPS));
  };

  const goBack = () => setStep((prev) => Math.max(prev - 1, 1));

  const submit = async () => {
    const startTimeForSubmit = resolvedStartTime;
    const stepErrors = validationForStep(2, {
      ...form,
      start_time: startTimeForSubmit,
    });
    if (Object.keys(stepErrors).length) {
      setErrors((prev) => ({ ...prev, ...stepErrors }));
      setStep(2);
      return;
    }
    const payload = {
      ...form,
      start_time: startTimeForSubmit,
      enquiry_id: form.enquiry_id || null,
      counsellor_id: Number(form.counsellor_id || effectiveCounsellorId || 0) || null,
      ...(form.appointment_type_id || fallbackAppointmentTypeId
        ? { appointment_type_id: Number(form.appointment_type_id || fallbackAppointmentTypeId) }
        : {}),
    };
    await onSubmit?.(payload);
  };

  const renderStepBody = () => {
    if (step === 1) {
      return (
        <div className={styles.stepBody}>
          <Row className="g-3">
            <Col md={12}>
              <button
                type="button"
                className={`${styles.manualEntryButton} ${manualEntryEnabled ? styles.manualEntryButtonActive : ""}`}
                onClick={() => setManualEntryEnabled((prev) => !prev)}
              >
                Manual
              </button>
              <p className={styles.manualEntryHint}>
                Student not currently in the system? Don&apos;t worry, use manual entry.
              </p>
            </Col>
            <Col md={12}>
              <Form.Label>Search Existing Enquiry</Form.Label>
              <Form.Control
                placeholder="Search by name, email, phone or enquiry ID"
                value={enquirySearch}
                onChange={(e) => setEnquirySearch(e.target.value)}
              />
              <div style={{ maxHeight: 210, overflowY: "auto", marginTop: 8, border: "1px solid #e5e7eb", borderRadius: 10 }}>
                {filteredEnquiries.length === 0 ? (
                  <div style={{ padding: 10, color: "#6b7280", fontSize: 13 }}>No enquiries found.</div>
                ) : (
                  filteredEnquiries.map((enquiry) => (
                    <button
                      type="button"
                      key={enquiry.id}
                      onClick={() => onSelectEnquiry(enquiry)}
                      style={{
                        width: "100%",
                        border: "none",
                        borderBottom: "1px solid #f3f4f6",
                        background: Number(form.enquiry_id) === Number(enquiry.id) ? "#eff6ff" : "#fff",
                        padding: "8px 10px",
                        textAlign: "left",
                      }}
                    >
                      <div style={{ fontWeight: 600 }}>
                        #{enquiry.id} - {enquiry.first_name} {enquiry.last_name}
                      </div>
                      <small style={{ color: "#6b7280" }}>{enquiry.email || "--"} | {enquiry.phone_number || "--"}</small>
                    </button>
                  ))
                )}
              </div>
            </Col>

            {manualEntryEnabled ? (
              <>
                <Col md={6}>
                  <Form.Label>Customer Name</Form.Label>
                  <Form.Control
                    value={form.customer_name}
                    onChange={(e) => setField("customer_name", e.target.value)}
                    isInvalid={Boolean(errors.customer_name)}
                  />
                  {errors.customer_name ? <div className={styles.errorText}>{errors.customer_name}</div> : null}
                </Col>
                <Col md={6}>
                  <Form.Label>Customer Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={form.customer_email}
                    onChange={(e) => setField("customer_email", e.target.value)}
                    isInvalid={Boolean(errors.customer_email)}
                  />
                  {errors.customer_email ? <div className={styles.errorText}>{errors.customer_email}</div> : null}
                </Col>
                <Col md={6}>
                  <Form.Label>Customer Phone</Form.Label>
                  <Form.Control value={form.customer_phone} onChange={(e) => setField("customer_phone", e.target.value)} />
                </Col>
                <Col md={6}>
                  <Form.Label>Enquiry ID</Form.Label>
                  <Form.Control value={form.enquiry_id || ""} readOnly />
                </Col>
              </>
            ) : (
              <Col md={12}>
                <div className={styles.selectionSummaryCard}>
                  <strong>Selected customer:</strong>{" "}
                  {form.customer_name ? `${form.customer_name} (${form.customer_email || "--"})` : "Please choose an enquiry above"}
                </div>
              </Col>
            )}
          </Row>
        </div>
      );
    }

    if (step === 2) {
      return (
        <div className={styles.stepBody}>
          <Row className="g-3">
            <Col md={12}>
              <Alert variant="light" style={{ marginBottom: 0 }}>
                Select a date and time slot from the calendar.
                <div style={{ marginTop: 4, color: "#334155" }}>
                  Selected start time: <strong>{resolvedStartTime || "--"}</strong>
                </div>
                {errors.start_time ? <div className={styles.errorText}>{errors.start_time}</div> : null}
              </Alert>
            </Col>

            <Col md={8}>
              <div className={styles.monthCalendarCard}>
                <div className={styles.monthHeader}>
                  <strong>{format(calendarMonth, "MMMM yyyy")}</strong>
                  <div className={styles.monthActions}>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setCalendarMonth((prev) => startOfMonth(addMonths(prev, -1)))}
                    >
                      Prev
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setCalendarMonth((prev) => startOfMonth(addMonths(prev, 1)))}
                    >
                      Next
                    </Button>
                  </div>
                </div>

                <div className={styles.weekdaysGrid}>
                  {WEEKDAY_LABELS.map((label) => (
                    <div key={label} className={styles.weekdayCell}>
                      {label}
                    </div>
                  ))}
                </div>

                <div className={styles.calendarGrid}>
                  {monthDays.map((day, index) => {
                    if (!day) return <div key={`empty-${index}`} className={styles.dayCellEmpty} />;
                    const dateKey = format(day, "yyyy-MM-dd");
                    const hasSlots = slotsByDate.has(dateKey);
                    const hasAvailable = availableDateKeys.has(dateKey);
                    const selected = selectedDateKey === dateKey;
                    const disabled = isBefore(day, new Date()) && !isSameDay(day, new Date());
                    return (
                      <button
                        type="button"
                        key={dateKey}
                        className={`${styles.dayCell} ${selected ? styles.dayCellSelected : ""} ${
                          hasAvailable ? styles.dayCellAvailable : ""
                        }`}
                        disabled={!hasSlots || disabled}
                        onClick={() => {
                          setSelectedDateKey(dateKey);
                          setSelectedSlotStartUtc("");
                          setField("start_time", "");
                        }}
                      >
                        <span>{getDate(day)}</span>
                        {hasAvailable && !selected ? <small className={styles.dayDot} /> : null}
                        {isToday(day) ? <small className={styles.todayTag}>Today</small> : null}
                      </button>
                    );
                  })}
                </div>
              </div>
            </Col>

            <Col md={4}>
              <div className={styles.timeSlotsPanel}>
                <h6 className={styles.slotPanelTitle}>Available Times</h6>
                {selectedDateSlots.length > 0 && selectedDateSlots[0].timezone ? (
                  <p className={styles.slotTimezoneHint}>Times in {selectedDateSlots[0].timezone}</p>
                ) : null}
                {isLoadingSlots ? (
                  <div style={{ textAlign: "center", padding: "14px 0" }}>
                    <Spinner animation="border" size="sm" />
                  </div>
                ) : selectedDateSlots.length === 0 ? (
                  <div className={styles.noSlotsText}>Select a date with available slots.</div>
                ) : (
                  <div className={styles.slotList}>
                    {selectedDateSlots.map((slot) => {
                      const startUtc = slot.start_time_utc;
                      const selected = selectedSlotStartUtc === startUtc;
                      const taken = Number(slot.available_capacity || 0) <= 0;
                      return (
                        <button
                          type="button"
                          key={`${slot.counsellor_id}-${startUtc}`}
                          className={`${styles.slotButton} ${selected ? styles.slotButtonSelected : ""}`}
                          disabled={taken}
                          onClick={() => {
                            setSelectedSlotStartUtc(slot.start_time_utc);
                            setForm((prev) => ({ ...prev, start_time: slot.start_time_utc }));
                            setErrors((prev) => ({ ...prev, start_time: undefined }));
                          }}
                        >
                          <span>
                            {formatTime(slot.start_time_utc, slot.timezone || form.timezone)} - {formatTime(slot.end_time_utc, slot.timezone || form.timezone)}
                          </span>
                          <small className={styles.slotStatus}>{taken ? "Taken" : "Available"}</small>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </div>
      );
    }

    if (step === 3) {
      return (
        <div className={styles.stepBody}>
          <Row className="g-3">
            <Col md={12}>
              <Form.Label>Internal Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={form.notes_internal}
                onChange={(e) => setField("notes_internal", e.target.value)}
                placeholder="Notes are internal only and not visible to the customer."
              />
            </Col>
            <Col md={12}>
              <Form.Check
                type="switch"
                id="is_private_switch"
                checked={Boolean(form.is_private)}
                onChange={(e) => setField("is_private", e.target.checked)}
                label="Private appointment (internal visibility)"
              />
            </Col>
          </Row>
        </div>
      );
    }

    return (
      <div className={styles.stepBody}>
        <div className={styles.reviewGrid}>
          <div className={styles.reviewItem}>
            <span className={styles.reviewLabel}>Customer</span>
            <span className={styles.reviewValue}>{form.customer_name || "--"}</span>
          </div>
          <div className={styles.reviewItem}>
            <span className={styles.reviewLabel}>Email</span>
            <span className={styles.reviewValue}>{form.customer_email || "--"}</span>
          </div>
          <div className={styles.reviewItem}>
            <span className={styles.reviewLabel}>Phone</span>
            <span className={styles.reviewValue}>{form.customer_phone || "--"}</span>
          </div>
          <div className={styles.reviewItem}>
            <span className={styles.reviewLabel}>Enquiry</span>
            <span className={styles.reviewValue}>{form.enquiry_id || "--"}</span>
          </div>
          {/* <div className={styles.reviewItem}>
            <span className={styles.reviewLabel}>Type</span>
            <span className={styles.reviewValue}>{selectedType?.name || "--"}</span>
          </div> */}
          <div className={styles.reviewItem}>
            <span className={styles.reviewLabel}>Counsellor ID</span>
            <span className={styles.reviewValue}>{form.counsellor_id || "Unassigned"}</span>
          </div>
          <div className={styles.reviewItem}>
            <span className={styles.reviewLabel}>Start</span>
            <span className={styles.reviewValue}>
              {resolvedStartTime
                ? `${formatTime(resolvedStartTime, form.timezone)} (${form.timezone})`
                : "--"}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Modal show={show} onHide={onHide} centered size="xl" backdrop="static">
      <Card className={styles.wizardCard}>
        <Card.Body className={styles.cardBody}>
          <div className={styles.gwHeader}>
            <div>
              <h4 className={styles.gwTitle}>Create Appointment Wizard</h4>
              <p className={styles.gwSubtitle}>Follow the guided flow to schedule a meeting with CRM-linked customer data.</p>
            </div>
            <div style={{ minWidth: 220 }}>
              <div className={styles.gwProgressText}>Step {step} of {TOTAL_STEPS}</div>
              <ProgressBar now={progressPercent} className={styles.gwLinearProgress} />
            </div>
          </div>

          <div className={styles.gwProgress}>
            {STEP_META.map((item) => (
              <div
                key={item.id}
                className={`${styles.gwStep} ${item.id === step ? styles.gwStepCurrent : ""} ${item.id < step ? styles.gwStepDone : ""}`}
              >
                <span className={styles.gwStepBadge}>{item.id < step ? <CheckCircleRoundedIcon style={{ fontSize: 14 }} /> : item.id}</span>
                <span className={styles.gwStepTitle}>{item.short}</span>
              </div>
            ))}
          </div>

          {apiError ? <Alert variant="danger">{apiError}</Alert> : null}
          {isLoadingData ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "1.2rem 0" }}>
              <Spinner animation="border" />
            </div>
          ) : (
            renderStepBody()
          )}

          <div className={styles.actions}>
            <Button variant="outline-secondary" className={styles.leftAction} onClick={onHide} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button variant="outline-secondary" onClick={goBack} disabled={step === 1 || isSubmitting}>
              Back
            </Button>
            {step < TOTAL_STEPS ? (
              <Button onClick={goNext} disabled={isSubmitting || isLoadingData}>
                Continue
              </Button>
            ) : (
              <Button onClick={submit} disabled={isSubmitting || isLoadingData}>
                {isSubmitting ? "Creating..." : "Create Appointment"}
              </Button>
            )}
          </div>
        </Card.Body>
      </Card>
    </Modal>
  );
}
